import { useState, useEffect, createContext, useContext } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

// Environment flag to enable/disable authentication
const USE_MOCK_AUTH = import.meta.env.VITE_USE_MOCK_AUTH === 'true';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (email: string, password: string, username?: string) => Promise<{ error: any }>;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signInWithGoogle: () => Promise<{ error: any }>;
  signInWithGitHub: () => Promise<{ error: any }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Mock user for public access
const mockUser: User = {
  id: 'mock-user-id',
  email: 'public@example.com',
  user_metadata: { username: 'Public User' },
  app_metadata: {},
  aud: 'authenticated',
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
  email_confirmed_at: new Date().toISOString(),
  last_sign_in_at: new Date().toISOString(),
  role: 'authenticated',
  confirmation_sent_at: null,
  confirmed_at: null,
  email_change_sent_at: null,
  new_email: null,
  invited_at: null,
  action_link: null,
  recovery_sent_at: null,
  phone: null,
  phone_confirmed_at: null,
  phone_change_sent_at: null,
  new_phone: null,
  identities: [],
  factors: []
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(USE_MOCK_AUTH ? mockUser : null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(!USE_MOCK_AUTH);

  useEffect(() => {
    if (USE_MOCK_AUTH) {
      // In mock mode, set up mock user immediately
      setLoading(false);
      return;
    }

    // Original Supabase authentication logic
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
      }
    );

    // 检查现有会话
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    }).catch((error) => {
      console.error('Error getting session:', error);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const getRedirectUrl = () => {
    // 获取当前域名
    const currentOrigin = window.location.origin;

    // 生产环境域名列表
    const productionDomains = [
      'https://redbook-cover-maker-b551cd60-27akk93r2-aihes-projects.vercel.app',
      'https://redbook-cover-maker-b551cd60.vercel.app',
      'https://xhs.cryptopoint.cc' // 自定义域名（如果有）
    ];

    // 如果是生产环境域名，直接使用当前域名
    if (productionDomains.some(domain => currentOrigin.includes(domain.replace('https://', '')))) {
      return currentOrigin + '/';
    }

    // 如果是 Vercel 预览域名
    if (currentOrigin.includes('vercel.app')) {
      return currentOrigin + '/';
    }

    // 如果是 Lovable 预览域名
    if (currentOrigin.includes('lovable.app')) {
      return currentOrigin + '/';
    }

    // 本地开发环境
    if (currentOrigin.includes('localhost')) {
      return 'http://localhost:3000/';
    }

    // 默认使用当前域名
    return currentOrigin + '/';
  };

  const signUp = async (email: string, password: string, username?: string) => {
    if (USE_MOCK_AUTH) {
      // In mock mode, always return success
      return { error: null };
    }

    const redirectUrl = getRedirectUrl();

    try {
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            username: username || email.split('@')[0]
          }
        }
      });

      if (error) {
        console.error('Sign up error:', error);
      }

      return { error };
    } catch (error) {
      console.error('Sign up error:', error);
      return { error };
    }
  };

  const signIn = async (email: string, password: string) => {
    if (USE_MOCK_AUTH) {
      // In mock mode, always return success
      return { error: null };
    }

    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        console.error('Sign in error:', error);
      }

      return { error };
    } catch (error) {
      console.error('Sign in error:', error);
      return { error };
    }
  };

  const signInWithGoogle = async () => {
    if (USE_MOCK_AUTH) {
      // In mock mode, always return success
      return { error: null };
    }

    const redirectUrl = getRedirectUrl();

    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectUrl
        }
      });

      if (error) {
        console.error('Google sign in error:', error);
      }

      return { error };
    } catch (error) {
      console.error('Google sign in error:', error);
      return { error };
    }
  };

  const signInWithGitHub = async () => {
    if (USE_MOCK_AUTH) {
      // In mock mode, always return success
      return { error: null };
    }

    const redirectUrl = getRedirectUrl();

    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'github',
        options: {
          redirectTo: redirectUrl
        }
      });

      if (error) {
        console.error('GitHub sign in error:', error);
      }

      return { error };
    } catch (error) {
      console.error('GitHub sign in error:', error);
      return { error };
    }
  };

  const signOut = async () => {
    if (USE_MOCK_AUTH) {
      // In mock mode, do nothing
      return;
    }

    try {
      await supabase.auth.signOut();
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  const value = {
    user,
    session,
    loading,
    signUp,
    signIn,
    signInWithGoogle,
    signInWithGitHub,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
